/**
 * Case Detail — full single-case view.
 *
 * Layout mirrors mail-workflow-detail: case summary at the top,
 * tabbed sections below (Overview, Parties, Documents, Events,
 * Tasks, Activity, Costs, Notes).
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CaseManagementService } from '../case-management.service';
import {
  LegalCase, CaseParty, CaseDocument, CaseEvent, CaseTask,
  CaseActivity, CaseCost, CaseNote,
  CASE_TYPE_LABELS, CASE_STATUS_LABELS, CASE_STATUS_COLORS,
  CASE_PRIORITY_COLORS, EVENT_TYPE_LABELS, EVENT_TYPE_ICONS,
  DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_ICONS
} from '../case-management.model';

type TabId = 'overview' | 'parties' | 'documents' | 'events' | 'tasks' | 'activity' | 'costs' | 'notes';

@Component({
  selector: 'sp-case-detail',
  templateUrl: './case-detail.component.html',
  styleUrls: ['./case-detail.component.scss']
})
export class CaseDetailComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  caseId  = '';
  case_:  LegalCase | null = null;
  loading = true;
  error   = '';

  // Tab data
  activeTab: TabId = 'overview';
  parties:    CaseParty[]    = [];
  documents:  CaseDocument[] = [];
  events:     CaseEvent[]    = [];
  tasks:      CaseTask[]     = [];
  activity:   CaseActivity[] = [];
  costs:      CaseCost[]     = [];
  notes:      CaseNote[]     = [];

  // Expose Math to template (used for cost-row abs())
  readonly Math = Math;

  // Constants for templates
  readonly TYPE_LABELS    = CASE_TYPE_LABELS;
  readonly STATUS_LABELS  = CASE_STATUS_LABELS;
  readonly STATUS_COLORS  = CASE_STATUS_COLORS;
  readonly PRIORITY_COLORS = CASE_PRIORITY_COLORS;
  readonly EVENT_LABELS   = EVENT_TYPE_LABELS;
  readonly EVENT_ICONS    = EVENT_TYPE_ICONS;
  readonly DOC_LABELS     = DOCUMENT_TYPE_LABELS;
  readonly DOC_ICONS      = DOCUMENT_TYPE_ICONS;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private caseService: CaseManagementService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(p => {
      this.caseId = p.get('id') || '';
      if (this.caseId) this.loadAll();
    });
    // Allow deep-link to a tab via URL fragment (#events, #docs, ...)
    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe(f => {
      const validTabs: TabId[] = ['overview','parties','documents','events','tasks','activity','costs','notes'];
      if (f && validTabs.includes(f as TabId)) this.activeTab = f as TabId;
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  loadAll(): void {
    this.loading = true;
    this.error = '';
    this.caseService.getCase(this.caseId).subscribe(c => {
      if (!c) { this.error = `Case ${this.caseId} not found`; this.loading = false; return; }
      this.case_ = c;
      this.loadTabs();
    });
  }

  private loadTabs(): void {
    let pending = 7;
    const done  = () => { if (--pending === 0) this.loading = false; };
    this.caseService.getParties(this.caseId).subscribe(d => { this.parties = d; done(); });
    this.caseService.getDocuments(this.caseId).subscribe(d => { this.documents = d; done(); });
    this.caseService.getEvents(this.caseId).subscribe(d => { this.events = d; done(); });
    this.caseService.getTasks(this.caseId).subscribe(d => { this.tasks = d; done(); });
    this.caseService.getActivity(this.caseId).subscribe(d => { this.activity = d; done(); });
    this.caseService.getCosts(this.caseId).subscribe(d => { this.costs = d; done(); });
    this.caseService.getNotes(this.caseId).subscribe(d => { this.notes = d; done(); });
  }

  switchTab(t: TabId): void { this.activeTab = t; }

  /** Reload a tab after an add from the actions/add panel (also refreshes the activity timeline). */
  reloadDocuments(): void { this.caseService.getDocuments(this.caseId).subscribe(d => this.documents = d); this.reloadActivity(); }
  reloadParties(): void  { this.caseService.getParties(this.caseId).subscribe(d => this.parties = d); this.reloadActivity(); }
  reloadNotes(): void    { this.caseService.getNotes(this.caseId).subscribe(d => this.notes = d); this.reloadActivity(); }
  reloadTasks(): void    { this.caseService.getTasks(this.caseId).subscribe(d => this.tasks = d); this.reloadActivity(); }
  reloadEvents(): void   { this.caseService.getEvents(this.caseId).subscribe(d => this.events = d); this.reloadActivity(); }
  reloadCosts(): void    { this.caseService.getCosts(this.caseId).subscribe(d => this.costs = d); this.reloadActivity(); }
  private reloadActivity(): void { this.caseService.getActivity(this.caseId).subscribe(d => this.activity = d); }

  // Computed: cost totals
  get totalCosts(): number {
    return this.costs.filter(c => c.amount > 0).reduce((sum,c) => sum + c.amount, 0);
  }
  get totalRecovered(): number {
    return Math.abs(this.costs.filter(c => c.amount < 0).reduce((sum,c) => sum + c.amount, 0));
  }
  get netPosition(): number {
    return this.totalRecovered - this.totalCosts;
  }

  // Tasks computed
  get openTaskCount(): number  { return this.tasks.filter(t => t.status !== 'DONE').length; }
  get overdueTaskCount(): number { return this.tasks.filter(t => t.status === 'OVERDUE').length; }

  // Events computed
  get upcomingEventCount(): number { return this.events.filter(e => e.status === 'UPCOMING' && e.scheduledAt > new Date()).length; }

  // ── Actions (demo placeholders) ──────────────────────────────────────────
  backToList(): void { this.router.navigate(['/case-management/cases']); }
  backToDashboard(): void { this.router.navigate(['/case-management']); }
  editCase(): void { alert('Edit case — opens edit drawer in production.'); }
  closeCase(): void { alert('Close case — opens disposition wizard (outcome, recovery, notes).'); }
  addEvent(): void { alert('Schedule a new event — opens event creation modal.'); }
  addTask(): void { alert('Add task — opens task creation modal.'); }
  addDocument(): void { alert('Upload document — Alfresco multipart upload (already wired in mail-workflow pattern).'); }
  addParty(): void { alert('Add party — opens add-party form.'); }
  addCost(): void { alert('Record cost / recovery — opens cost entry form.'); }
  addNote(): void { alert('Add note — opens privileged-note editor.'); }
  exportCase(): void { alert('Export case bundle (PDF) — backend assembles all docs + summary.'); }

  // ── Format helpers ───────────────────────────────────────────────────────
  formatCurrency(v: number, compact=false): string {
    if (compact && Math.abs(v) >= 1_000_000) return `KES ${(v/1_000_000).toFixed(2)}M`;
    if (compact && Math.abs(v) >= 1_000)     return `KES ${(v/1_000).toFixed(0)}K`;
    return new Intl.NumberFormat('en-KE', { style:'currency', currency:'KES', minimumFractionDigits:0, maximumFractionDigits:0 }).format(v);
  }
  formatBytes(b: number): string {
    if (b < 1024) return `${b} B`;
    if (b < 1024*1024) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/1024/1024).toFixed(1)} MB`;
  }
  formatDate(d?: Date): string {
    if (!d) return '—';
    return d.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
  }
  formatDateTime(d: Date): string {
    return d.toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  }
  formatTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60)    return 'Just now';
    if (seconds < 3600)  return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
  }
  daysUntil(d?: Date): number | null {
    if (!d) return null;
    return Math.ceil((d.getTime() - new Date().getTime()) / 86400000);
  }
  urgencyClass(d?: Date): string {
    const n = this.daysUntil(d);
    if (n === null) return '';
    if (n < 0)  return 'urg-overdue';
    if (n <= 2) return 'urg-critical';
    if (n <= 7) return 'urg-warning';
    return 'urg-normal';
  }

  activityIcon(category: string): string {
    const icons: Record<string,string> = {
      OPENED:'add_circle', CLOSED:'check_circle',
      DOCUMENT:'description', EVENT:'event',
      TASK:'check_box', PARTY:'group',
      NOTE:'sticky_note_2', COST:'payments', STATUS:'swap_horiz'
    };
    return icons[category] || 'history';
  }
  partyRoleLabel(role: string): string {
    const map: Record<string,string> = {
      PLAINTIFF: 'Plaintiff',
      DEFENDANT: 'Defendant',
      WITNESS:   'Witness',
      COUNSEL_PLAINTIFF: 'Plaintiff Counsel',
      COUNSEL_DEFENDANT: 'Defendant Counsel',
      JUDGE:     'Judge',
      EXPERT:    'Expert',
      OTHER:     'Other'
    };
    return map[role] || role;
  }
  partyRoleClass(role: string): string {
    if (role === 'PLAINTIFF' || role === 'COUNSEL_PLAINTIFF') return 'role-plaintiff';
    if (role === 'DEFENDANT' || role === 'COUNSEL_DEFENDANT') return 'role-defendant';
    if (role === 'JUDGE') return 'role-judge';
    if (role === 'WITNESS' || role === 'EXPERT') return 'role-witness';
    return 'role-other';
  }
  taskStatusClass(s: string): string { return `status-${s.toLowerCase()}`; }
  eventStatusClass(s: string): string { return `event-${s.toLowerCase()}`; }
}
